import { createSelector } from 'reselect';
import {
  nodeHealthPageReducer,
  nodeIssuesReducer
} from '../reducers';
import { flatten, mapValues, uniq, values, keys } from 'lodash';
import {
  actionTypes as actionConstants,
  nodeIssues as nodeIssueHelpers,
  utils
} from '../helpers';
import * as actionItem from '../helpers/actionItem';

export const activeNodeIssueTypeIds = createSelector(
  nodeIssuesReducer.getActiveNodeIssues,
  (activeIssues) => uniq(activeIssues.map(i => i.issue_id))
);

export const activeNodeIssueJobNames = createSelector(
  nodeIssuesReducer.getActiveNodeIssues,
  (activeIssues) => uniq(activeIssues.map(i => i.job_name))
);


export const filteredActiveNodeIssueTypeIds = createSelector(
  activeNodeIssueTypeIds,
  nodeHealthPageReducer.getNodeHealthInfo,
  (activeIssues, filtered) =>
    activeIssues.filter(issue => filtered.issueTypeIds.includes(issue))
);

export const nodesWithActiveIssues = createSelector(
  nodeIssuesReducer.getActiveNodeIssues,
  (activeIssues) => (uniq(activeIssues.map(i => i.node_name ||
    nodeIssueHelpers.UNKNOWN_NODE)))
);

export const filteredNodeIssueTypesById = createSelector(
  (state) => nodeIssuesReducer.nodeIssueTypesById(state),
  nodeHealthPageReducer.getNodeHealthInfo,
  (issueTypesByIds, filtered) => {

    const filteredIssueTypesById = Object.keys(issueTypesByIds)
      .filter(key => filtered.issueTypeIds.includes(key))
      .reduce((obj, key) => {
        obj[key] = issueTypesByIds[key];
        return obj;
      }, {});

    return filteredIssueTypesById;
  }
);

export const getFilteredActiveNodeIssues = createSelector(
  nodeIssuesReducer.getActiveNodeIssues,
  nodeHealthPageReducer.getNodeHealthInfo,
  (activeIssues, filtered) => {
    const filteredIssues = filtered.issueTypeIds;
    const filteredNodes = filtered.nodes;
    const filteredJobNames = filtered.jobNames;
    return activeIssues.filter(issue => filteredIssues.includes(issue.issue_id) &&
    filteredJobNames.includes(issue.job_name) &&
    (filteredNodes.includes(issue.node_name) ||
    (filtered.nodes.includes(nodeIssueHelpers.UNKNOWN_NODE) &&
    issue.node_name === null)));
  }
);

/**
 * Groups the active issues into new object where keys are
 * their respective node_name values
 * * If the node_name value is null, then issues are default
 * * into the 'unknown' key
 */
export const activeIssuesByNode = createSelector(
  getFilteredActiveNodeIssues,
  (activeIssues) => {
    const result = utils.accumulatorReducerByFunc(
      activeIssues,
      o => nodeIssueHelpers.getIssueNodeName(o),
      o => o
    );
    return result;
  }
);

/**
 * Groups the active issues into new object where keys are
 * their respective issue_id values
 */
export const activeIssuesByTypeId = createSelector(
  getFilteredActiveNodeIssues,
  (activeIssues) => {
    const result = utils.accumulatorReducerByFunc(
      activeIssues,
      o => o.issue_id,
      o => o
    );
    return result;
  }
);

/**
 * Get the array of the issue types which are sort by numbers desc.
 */
export const issueTypeIdsSortByTypeNumbersDesc = createSelector(
  activeIssuesByTypeId,
  (activeIssuesByType) => {
    const issueTypes = keys(activeIssuesByType);
    issueTypes.sort((a, b) =>
    { return activeIssuesByType[b].length - activeIssuesByType[a].length; });
    return issueTypes;
  }
);

/**
 * Groups active issues by the issueId they represent at top level then group further
 * by the name of the node where the issue was encountered
 * The grouped active issues are sorted by issue type numbers desc
 * Returns object of design:
 * {
 *  [issueIdN]: {
 *    [nodeA]: [],
 *    [nodeB]: []
 * }
 */
export const activeIssuesByNodeByIdSortbyTypeNumbersDesc = createSelector(
  activeIssuesByNode,
  issueTypeIdsSortByTypeNumbersDesc,
  (activeIssuesByNode, issueTypeIds) => {
    const issues = flatten(values(activeIssuesByNode));
    if (issues.length === 0 || issueTypeIds.length === 0) {
      return {};
    }

    // a conceptual row, headed by node names
    const emptyArrayByNodeNames = mapValues(
      { ...activeIssuesByNode, [nodeIssueHelpers.UNKNOWN_NODE]: {}},
      () => []
    );

    // a conceptual table, rows of issue types, columns of nodes
    const emptyissueTypesByNodes = issueTypeIds.reduce((acc, id) => {
      acc[id] = { ...emptyArrayByNodeNames };
      return acc;
    }, {});

    const result = issues.reduce((acc, issue) => {
      const nodeName = nodeIssueHelpers.getIssueNodeName(issue);
      const prev = acc[issue.issue_id][nodeName];
      acc[issue.issue_id][nodeName] = [...prev, issue];
      return acc;
    }, emptyissueTypesByNodes);

    return result;
  }
);

/**
 * Groups active issues by the node they represent at top level then group further
 * by the issue type where the issue was encountered
 * The grouped active issues are sorted by issue type numbers desc
 * Returns object of design:
 * {
 *  [nodeN]: {
 *    [issueIdA]: [],
 *    [issueIdB]: []
 * }
 */
export const activeIssuesByIdSortByTypeNumbersDescByNode = createSelector(
  activeIssuesByNode,
  (activeIssuesByNode) => {
    const nodes = keys(activeIssuesByNode);
    const sortedResult = nodes.reduce((result, node) => {
      result[node] = {};
      const activeIssues = activeIssuesByNode[node];
      const activeIssuesByType = utils.accumulatorReducerByFunc(
        activeIssues,
        o => o.issue_id,
        o => o
      );
      const issueTypes = keys(activeIssuesByType);
      issueTypes.sort((a, b) =>
      { return activeIssuesByType[b].length - activeIssuesByType[a].length; });
      issueTypes.forEach(typeId =>
      { result[node][typeId] = activeIssuesByType[typeId]; });
      return result;
    }, {});
    return sortedResult;
  }
);

/**
 * Groups active issues by the issueId they represent at top level then group further
 * by the name of the node where the issue was encountered
 * The grouped active issues are sorted by issue type numbers desc
 *  and node numbers desc by default
 * Returns object of design:
 * {
 *  [issueIdN]: {
 *    [nodeA]: [],
 *    [nodeB]: []
 * }
 */
export const activeIssuesByNodeNameById = createSelector(
  activeIssuesByNodeByIdSortbyTypeNumbersDesc,
  (issuesByNodeByTypeId) => {
    const typeIds = keys(issuesByNodeByTypeId);
    const orderedActiveIssuesByNodeById = typeIds.reduce((orderedResult, typeId) => {
      orderedResult[typeId] = {};
      const issuesByNode = issuesByNodeByTypeId[typeId];
      const nodes = keys(issuesByNode);
      nodes.sort((a, b) => { return issuesByNode[b].length - issuesByNode[a].length; });
      nodes.forEach(node => {
        orderedResult[typeId][node] = issuesByNode[node] ;
      });
      return orderedResult;
    }, {});

    return orderedActiveIssuesByNodeById;
  }
);
/**
 * Returns an object where the keys are all tracked node's names
 * or issue ids and the values are their respective counts for each.
 * Also available a grand total
 */
export const activeIssueCountsLookup = createSelector(
  getFilteredActiveNodeIssues,
  (activeIssues) => {
    const result = activeIssues.reduce((lookup, issue) => {
      const nodeName = nodeIssueHelpers.getIssueNodeName(issue);

      const lookupNode = lookup[nodeName] || 0;
      lookup[nodeName] = lookupNode + 1;

      const lookupIssueId = lookup[issue.issue_id] || 0;
      lookup[issue.issue_id] = lookupIssueId + 1;

      lookup.total++;
      return lookup;
    }, { total: 0 });

    return result;
  }
);

/**
 * Unravels the selected table cell data and returns an array of
 * active issues that is represented by the selection
 */
export const getSelectedNodeIssues = createSelector(
  (state) => nodeHealthPageReducer.getNodeHealthSelectedSummaryCells(state),
  activeIssuesByNode,
  activeIssuesByTypeId,
  activeIssuesByNodeNameById,
  (selectedCells, byNode, byTypeId, byNodeByTypeId) => {
    // map the data array into activeIssues, group selections must be accounted
    const mappedSelection = selectedCells && selectedCells.map(cellData => {
      const group = cellData.group;
      if (group === 'total') {
        return flatten(values(byTypeId));
      }
      if (group) {
        const activeIssues = byNode[group] || byTypeId[group];
        return activeIssues || [];
      }

      return (byNodeByTypeId[cellData.issueId] &&
      byNodeByTypeId[cellData.issueId][cellData.nodeName]) || [];
    });

    const result = uniq(
      flatten(mappedSelection),
      (issue) => issue.id
    );
    return result;
  }
);

export const getNodeHealthIssueJobLinksById = createSelector(
  nodeIssuesReducer.activeNodeIssuesById,
  (activeIssuesById) => {
    const ids = keys(activeIssuesById);
    const jobLinksById = ids.reduce((result, id) => {
      const activeIssue = activeIssuesById[id];
      if (activeIssue === null)
      {
        return result;
      }
      const server = activeIssue.server;
      const jobName = activeIssue.job_name;
      const jobNumber = activeIssue.job_number;
      const jobFolder = activeIssue.job_folder;
      const jobLink = jobFolder ?
        `https://${server}/job/${jobFolder}/job/${jobName}/${jobNumber}` :
        `https://${server}/job/${jobName}/${jobNumber}`;
      result[id] = jobLink;
      return result;
    }, {});

    return jobLinksById;
  }
);

export const getNodeHealthActionsById = createSelector(
  nodeIssuesReducer.activeNodeIssuesById,
  (activeIssuesById) => {
    const ids = keys(activeIssuesById);
    const actionsById = ids.reduce((result, id) => {
      const activeIssue = activeIssuesById[id];
      if (activeIssue === null)
      {
        return result;
      }
      const server = activeIssue.server;
      const jobName = activeIssue.job_name;
      const jobNumber = activeIssue.job_number;
      const jobFolder = activeIssue.job_folder;
      const issueTypeId = activeIssue.issue_id;
      const actions = [{
        title: actionConstants.OPEN_JOB_URL,
        onSelect: actionItem._openNodeIssueJobUrl(server, jobName, jobNumber, jobFolder)
      },
      {
        title: actionConstants.OPEN_JIRA_URL,
        onSelect: actionItem._openJiraTicketUrl(issueTypeId)
      }];
      result[id] = actions;
      return result;
    }, {});
    return actionsById;
  }
);

/**
 * Returns an array of the node_issues selected for multi-acknowledgement
 */
export const getNodeIssuesSelectedForAck = createSelector(
  (state) => nodeHealthPageReducer.getNodeHealthSelectedForAck(state),
  (selectedForAck) => {
    return selectedForAck.map(sel => parseInt(sel.id));
  }
);


// WEBPACK FOOTER //
// src/selectors/nodeIssues.js